import './CustomSegments.scss';
import { useMemo, useState } from 'react';
import { Form, OverlayTrigger, Popover } from 'react-bootstrap';
import cn from 'classnames';
import { MdAdd, MdEdit, MdDelete } from 'react-icons/md';
import { BILLING_SEGMENT_NAMES } from '@blisspointmedia/bpm-types/dist/SegmentationMapping';
import { Button } from '@Components/Button';
import { Radio } from '@Components/FormCheck';
import { IconButton } from '@Components/IconButton';
import { InfoTooltip } from '@Components/InfoTooltip';
import { DropdownMenu } from '@Components/Dropdown';
import { Appearance, Placement, Size } from '@Components/designConstants';
import {
  CommittedValue,
  NewValue,
  PRESET_VALUES_MAP,
  PresetSegmentNames,
  SystemGeneratedSegmentNames
} from './segmentation-mapping-constants';

export const SegmentType = {
  SYSTEM_GENERATED: 'system-generated',
  REQUIRED: 'required',
  BILLING: 'billing',
  PRESET: 'preset',
  CUSTOM: 'custom'
} as const;
export type SegmentTypeValue = (typeof SegmentType)[keyof typeof SegmentType];

interface CustomSegmentCardProps {
  type: SegmentTypeValue;
  id: number | string;
  name: string;
  values: (CommittedValue | NewValue)[];
  dataGranularity: 'ad' | 'ad_group' | 'campaign';
  isInEditMode: boolean;
  isAdmin: boolean;
  hasEdits: boolean;
  setIsInEditMode?: (newVal: boolean) => void;
  onEditSegmentName: (newVal: string) => void;
  onEditSegmentValueName: (newVal: string, valueId: string) => void;
  addNewSegmentValue: (valueName: string, segmentId: number | string) => void;
  deleteSegment: () => void;
  deleteSegmentValue: (segmentId: string, valueId: number | string) => void;
}

export const CustomSegmentCard = ({
  type,
  id,
  name,
  values,
  dataGranularity,
  isInEditMode,
  isAdmin,
  hasEdits,
  setIsInEditMode = () => {},
  onEditSegmentName,
  onEditSegmentValueName,
  addNewSegmentValue,
  deleteSegment,
  deleteSegmentValue
}: CustomSegmentCardProps) => {
  const addBlankSegmentValue = () => addNewSegmentValue('', id);

  const segmentTypeLabel = (() => {
    switch (type) {
      case SegmentType.SYSTEM_GENERATED:
        return 'System Generated';
      case SegmentType.REQUIRED:
        return 'Required';
      case SegmentType.BILLING:
        return 'Billing';
      case SegmentType.PRESET:
        return 'Pre-set Segment';
      case SegmentType.CUSTOM:
      default:
        return 'Custom Segment';
    }
  })();

  const segmentNameTooltip = (() => {
    const tooltipCopyEnding = {
      [SegmentType.SYSTEM_GENERATED]: ` is a required label and will be automatically labeled for each of your ${dataGranularity}s but can be re-labeled if needed.`,
      [SegmentType.BILLING]: ` is used in automated billing calculations for each of your ${dataGranularity}s.`,
      [SegmentType.REQUIRED]: ` is a required label for each of your ${dataGranularity}s.`,
      [SegmentType.PRESET]: ` is optional for any ${dataGranularity} and includes standardized values approved by Client Reporting. These values are not editable.`
    };

    switch (true) {
      case name === SystemGeneratedSegmentNames.PLATFORM:
        return `${name} is the system through which the ad impression is delivered (e.g. Meta). ${name}${
          tooltipCopyEnding[SegmentType.SYSTEM_GENERATED]
        }`;
      case name === SystemGeneratedSegmentNames.CHANNEL:
        return `${name} is a type of marketing effort (e.g. Paid Social). ${name}${
          tooltipCopyEnding[SegmentType.SYSTEM_GENERATED]
        }`;
      case name === BILLING_SEGMENT_NAMES[0]:
        return `${name} tracks whether a given campaign is eligible for Tinuiti to charge a fee for billing purposes. ${name}${
          tooltipCopyEnding[SegmentType.BILLING]
        }`;
      case name === BILLING_SEGMENT_NAMES[1]:
        return `${name} tracks whether a campaign is managed by Tinuiti or not for billing purposes. ${name}${
          tooltipCopyEnding[SegmentType.BILLING]
        }`;
      case name === PresetSegmentNames.BILLING_CATEGORY:
        return `${name} corresponds to "Brand/Country" field in billing sheets. Label your campaigns with the same values that your client uses for "Brand/Country" to separate your bills into different line items as needed. ${name}${
          tooltipCopyEnding[SegmentType.BILLING]
        }`;
      case name === PresetSegmentNames.TACTIC:
        return `${name} is the primary strategy or audience for your spend. ${name}${
          tooltipCopyEnding[SegmentType.PRESET]
        }`;
      case name === PresetSegmentNames.FUNNEL_TIER:
        return `${name} is the type of response a campaign is trying to generate (awareness, traffic or direct response). ${name}${
          tooltipCopyEnding[SegmentType.PRESET]
        }`;
      case name === PresetSegmentNames.PROMO:
        return `${name} is sales and evergreen periods. ${name}${
          tooltipCopyEnding[SegmentType.PRESET]
        }`;
      case name === PresetSegmentNames.BUDGET_TYPE:
        return `${name} is the part of the client's business that has to account for this campaign's media spend in its budget. ${name}${
          tooltipCopyEnding[SegmentType.PRESET]
        }`;
      case name === PresetSegmentNames.GEO:
        return `${name} is country-level campaign tracking. ${name}${
          tooltipCopyEnding[SegmentType.PRESET]
        }`;
      default:
        return 'Custom Segments are optional for any campaign, the values are editable and you can select from any of the values you populate here or N/A on the labeling page.';
    }
  })();

  const [nameValidationError, setNameValidationError] = useState<Record<string, string>>({});

  const validateName = (value: string, valueIdToValidate: string): boolean => {
    if (value.includes("'")) {
      setNameValidationError(current => ({
        ...current,
        [valueIdToValidate]: 'Segments and values cannot contain apostrophes.'
      }));
      return false;
    }
    if (value.includes('\\')) {
      setNameValidationError(current => ({
        ...current,
        [valueIdToValidate]: 'Segments and values cannot contain backslashes.'
      }));
      return false;
    }
    setNameValidationError({});
    return true;
  };

  const editableValues = useMemo(() => {
    if (type === SegmentType.CUSTOM && isInEditMode) {
      return values;
    }

    if ((type === SegmentType.PRESET && isAdmin) || name === PresetSegmentNames.BILLING_CATEGORY) {
      const presetValues =
        name === PresetSegmentNames.GEO
          ? GeoCountryOptions.map(country => country.label)
          : (PRESET_VALUES_MAP[name] ?? []);
      const approvedValues = [...presetValues, 'N/A'];

      return values.filter(({ valueName }) => !approvedValues.includes(valueName));
    }

    return [];
  }, [isAdmin, isInEditMode, name, type, values]);

  const nonEditableValues = useMemo(
    () => values.filter(val => !editableValues.includes(val)),
    [editableValues, values]
  );

  return (
    <div
      className={cn('segment-container', {
        edit: isInEditMode,
        default: type === SegmentType.SYSTEM_GENERATED || type === SegmentType.REQUIRED
      })}
    >
      <div className='segment-header'>
        <div className='segment-title'>
          <div className='segment-type-label'>
            {segmentTypeLabel}
            {type === SegmentType.CUSTOM && (
              <InfoTooltip>
                <p className='custom-segments-tooltip'>{segmentNameTooltip}</p>
              </InfoTooltip>
            )}
          </div>
          {(type === SegmentType.PRESET ||
            type === SegmentType.CUSTOM ||
            name === PresetSegmentNames.BILLING_CATEGORY) && (
            <div className='segment-controls'>
              <IconButton
                appearance={Appearance.EMPTY}
                onClick={
                  isInEditMode
                    ? () => {
                        deleteSegment();
                        setIsInEditMode(false);
                      }
                    : () => {
                        setIsInEditMode(true);
                      }
                }
              >
                {isInEditMode ? <MdDelete /> : <MdEdit />}
              </IconButton>
            </div>
          )}
        </div>
        {type === SegmentType.CUSTOM && isInEditMode ? (
          <Form.Group className='segment-name-input-group'>
            <Form.Control
              className='segment-name-input'
              size='sm'
              placeholder='New Segment'
              type='text'
              value={name}
              isInvalid={Boolean(nameValidationError[id])}
              onChange={e => {
                if (validateName(e.currentTarget.value, String(id))) {
                  onEditSegmentName(e.currentTarget.value);
                }
              }}
              onBlur={e => {
                onEditSegmentName(e.currentTarget.value.trim());
              }}
            />
            {nameValidationError[id] && (
              <Form.Control.Feedback type='invalid'>
                {nameValidationError[id]}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        ) : (
          <div className='segment-name-container'>
            <div className='segment-name'>{name}</div>
            {type !== SegmentType.CUSTOM && segmentNameTooltip && (
              <InfoTooltip>
                <p className='custom-segments-tooltip'>{segmentNameTooltip}</p>
              </InfoTooltip>
            )}
          </div>
        )}
      </div>
      <div className='segment-body'>
        <div className='segment-values'>
          {(isInEditMode ? nonEditableValues : [...nonEditableValues, ...editableValues]).map(
            ({ valueName }) => (
              <div className='option' key={valueName}>
                {valueName}
              </div>
            )
          )}
          {isInEditMode &&
            editableValues.map(({ valueId, valueName }) => (
              <div className='editable-value' key={valueId}>
                <Form.Group className='segment-name-input-group'>
                  <div className='segment-value-input-row'>
                    <Form.Control
                      className='segment-value-input'
                      size='sm'
                      placeholder='New Value'
                      type='text'
                      value={valueName}
                      isInvalid={Boolean(nameValidationError[valueId])}
                      onChange={e => {
                        if (validateName(e.currentTarget.value, String(valueId))) {
                          onEditSegmentValueName(e.currentTarget.value, String(valueId));
                        }
                      }}
                      onBlur={e => {
                        onEditSegmentValueName(e.currentTarget.value.trim(), String(valueId));
                      }}
                    />
                    <IconButton
                      appearance={Appearance.EMPTY}
                      onClick={() => {
                        deleteSegmentValue(String(id), valueId);
                      }}
                    >
                      <MdDelete />
                    </IconButton>
                  </div>
                  {nameValidationError[valueId] && (
                    <Form.Control.Feedback type='invalid'>
                      {nameValidationError[valueId]}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </div>
            ))}
          {(type === SegmentType.CUSTOM || name === PresetSegmentNames.BILLING_CATEGORY
            ? isInEditMode
            : isAdmin && isInEditMode && name !== PresetSegmentNames.GEO) && (
            <Button size={Size.LG} leadingElement={<MdAdd />} onClick={addBlankSegmentValue} />
          )}
        </div>
      </div>
      {hasEdits && (
        <div className='unsaved-changes-container'>
          <div className='unsaved-changes'>Unsaved changes</div>
        </div>
      )}
      {name === PresetSegmentNames.GEO && isInEditMode && (
        <GeoSelector
          selectedGeoValues={values.map(val => val.valueName)}
          onAddValue={(value: string) => {
            if (!values.map(val => val.valueName).includes(value)) {
              addNewSegmentValue(value, id);
            }
          }}
          onRemoveValue={(value: string) => {
            const valueToRemove = values.find(val => val.valueName === value);
            // Only allow removing unsaved values (string valueId)
            if (valueToRemove && typeof valueToRemove.valueId === 'string') {
              deleteSegmentValue(String(id), valueToRemove.valueId);
            }
          }}
        />
      )}
    </div>
  );
};

// Highly used countries on top, then ordered alphabetically for less common cases
const GeoCountryOptions = [
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'AU', label: 'Australia' },
  { value: 'AT', label: 'Austria' },
  { value: 'BE', label: 'Belgium' },
  { value: 'BR', label: 'Brazil' },
  { value: 'CZ', label: 'Czechia' },
  { value: 'EG', label: 'Egypt' },
  { value: 'FI', label: 'Finland' },
  { value: 'FR', label: 'France' },
  { value: 'DE', label: 'Germany' },
  { value: 'GR', label: 'Greece' },
  { value: 'DK', label: 'Denmark' },
  { value: 'HU', label: 'Hungary' },
  { value: 'IN', label: 'India' },
  { value: 'IE', label: 'Ireland' },
  { value: 'IT', label: 'Italy' },
  { value: 'JP', label: 'Japan' },
  { value: 'MX', label: 'Mexico' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'NZ', label: 'New Zealand' },
  { value: 'NO', label: 'Norway' },
  { value: 'PL', label: 'Poland' },
  { value: 'PT', label: 'Portugal' },
  { value: 'SA', label: 'Saudi Arabia' },
  { value: 'SG', label: 'Singapore' },
  { value: 'ES', label: 'Spain' },
  { value: 'SE', label: 'Sweden' },
  { value: 'CH', label: 'Switzerland' },
  { value: 'TR', label: 'Turkey' },
  { value: 'AE', label: 'United Arab Emirates' }
];

interface GeoSelectorProps {
  onAddValue: (value: string) => void;
  onRemoveValue: (value: string) => void;
  selectedGeoValues?: string[];
}

export const GeoSelector = ({
  onAddValue,
  onRemoveValue,
  selectedGeoValues = []
}: GeoSelectorProps) => {
  const geoMenuItems = GeoCountryOptions.map(country => ({
    value: country.value,
    label: country.label,
    selected: selectedGeoValues.includes(country.label)
  }));

  return (
    <OverlayTrigger
      placement={Placement.TOP}
      trigger='click'
      rootClose
      container={document.body}
      overlay={
        <Popover className='geo-selector-popover'>
          <Popover.Body>
            <DropdownMenu
              className='geo-selector-menu'
              title='Geo(s)'
              applyOnClick={null}
              cancelOnClick={null}
              menuItems={geoMenuItems}
              itemOnClick={({ label, selected }) => {
                if (label) {
                  if (selected) {
                    onAddValue(label);
                  } else {
                    onRemoveValue(label);
                  }
                }
              }}
              hasFilterBar
              multiSelect
              standalone
            />
          </Popover.Body>
        </Popover>
      }
    >
      <Button size={Size.LG} leadingElement={<MdAdd />} />
    </OverlayTrigger>
  );
};

interface NewSegmentCardProps {
  unaddedPresets: string[];
  addNewSegment: (key: string) => void;
}

export const NewSegmentCard = ({ unaddedPresets, addNewSegment }: NewSegmentCardProps) => {
  const [newSegmentSelection, setNewSegmentSelection] = useState('');

  return (
    <div className='segment-container'>
      <div className='segment-header'>
        <div className='segment-name'>Choose New Segment</div>
      </div>
      <div className='segment-body'>
        <div className='segment-values'>
          {unaddedPresets.map(presetKey => {
            const presetName = (PresetSegmentNames as Record<string, string>)[presetKey];

            return (
              <Radio
                key={presetKey}
                id={presetKey}
                checked={newSegmentSelection === presetKey}
                onChange={() => {
                  setNewSegmentSelection(presetKey);
                }}
                label={presetName}
              />
            );
          })}
          {unaddedPresets.length > 0 && <div className='segment-option-divider' />}
          <Radio
            id='custom'
            checked={newSegmentSelection === 'custom'}
            onChange={() => {
              setNewSegmentSelection('custom');
            }}
            label='Custom'
          />
        </div>
      </div>
      <div className='segment-footer'>
        <Button
          size={Size.LG}
          leadingElement={<MdAdd />}
          onClick={() => {
            addNewSegment(newSegmentSelection === 'custom' ? '' : newSegmentSelection);
            setNewSegmentSelection('');
          }}
          disabled={!newSegmentSelection}
        >
          Create
        </Button>
      </div>
    </div>
  );
};
